import { useEffect, useState } from "react";
import Filter from "../Components/Filter/Filter";
import { updateTask } from "../Services/pm_service";
import { DataTable, Page } from "@iq/models";
import { listProjectTasks } from "../Services/pm_service";
import ViewTaskModal from "../Components/modals/ViewTaskModal";

function formatDashboardDateTime(value) {
    if (!value || value === "-" || value === "N/A") {
        return "-";
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return String(value);
    }

    return parsed.toLocaleString();
}

const TIMELINE_TABLE_CONFIG = {
    columns: [
        {
            key: "title",
            label: "Task",
            accessor: (row) => row?.title || "Untitled task",
            filterable: false,
            width: "1.5fr",
        },
        {
            key: "status",
            label: "Status",
            accessor: (row) => row?.status || row?.task_status || row?.taskStatus || "-",
            filterable: false,
            width: "1fr",
        },
        {
            key: "priority",
            label: "Priority",
            accessor: (row) => row?.priority || "-",
            filterable: true,
            quickValues: ["Low", "Medium", "High", "Urgent"],
            width: ".75fr",
        },
        {
            key: "assignee",
            label: "Assignee",
            accessor: (row) => row?.assigned_user?.name || row?.assignedUser?.name || row?.assignee_name || "Unassigned",
            filterable: false,
            width: "1fr",
        },
        {
            key: "dueDate",
            label: "Due Date",
            accessor: (row) => formatDashboardDateTime(row?.due_date || row?.dueDate),
            filterable: true,
            quickValues: ["Most Recent", "Oldest"],
        },
    ],
};

function TimelinePage({ setPageHeader, selectedProject, setSelectedProject }) {
    const [tasks, setTasks] = useState([]);
    const [selectedTask, setSelectedTask] = useState(null);
    const selectedProjectId = selectedProject?.id ?? null;
    const [projectsLoading, setProjectsLoading] = useState(false);
    const [tasksLoading, setTasksLoading] = useState(false);
    const [createProjectOpen, setCreateProjectOpen] = useState(false);
    const [viewProjectOpen, setViewProjectOpen] = useState(false);
    const [filteredTasks, setFilteredTasks] = useState([]);

    const timelineOfTasks = [...(filteredTasks.length ? filteredTasks : tasks)].sort(
        (a, b) => new Date(b.due_date || b.dueDate) - new Date(a.due_date || a.dueDate)
    );

    useEffect(() => {
        if (!selectedProjectId) {
            setTasks([]);
            setTasksLoading(false);
            return;
        }

        let cancelled = false;

        async function loadTasks() {
            setTasksLoading(true);
            try {
                const data = await listProjectTasks(selectedProjectId);
                if (!cancelled) {
                    setTasks(Array.isArray(data) ? data : []);
                }
            } catch (error) {
                console.error("Failed to load PM tasks", error);
                if (!cancelled) {
                    setTasks([]);
                }
            } finally {
                if (!cancelled) {
                    setTasksLoading(false);
                }
            }
        }

        loadTasks();
        return () => {
            cancelled = true;
        };
    }, [selectedProjectId]);

    useEffect(() => {
        setFilteredTasks(tasks);
    }, [tasks]);

    async function refreshTasksForSelectedProject() {
        if (!selectedProjectId) {
            setTasks([]);
            return;
        }

        try {
            const data = await listProjectTasks(selectedProjectId);
            setTasks(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Failed to refresh PM tasks", error);
        }
    }

    async function handleMove(move) {
        if (!move || move.fromColumnId === move.toColumnId) {
            return;
        }

        const taskId = Number(move.itemId);
        if (!Number.isFinite(taskId)) {
            throw new Error("Invalid task id in PM kanban move payload");
        }

        await updateTask(taskId, {
            status: move.toColumnId,
            sortOrder: move.destinationIndex,
        });
    }

    useEffect(() => {
        setPageHeader?.({
            title: "Timeline",
            back: false,
            onBack: null,
            children: (
                <Filter
                    tasks={timelineOfTasks}
                    me
                    selectedProject={selectedProject}
                    onFilteredTasksChange={setFilteredTasks}
                    onProjectChange={(project) => {
                        setSelectedProject(project);
                    }}
                    tasksLoading={tasksLoading}
                    projectsLoading={projectsLoading}
                    onCreateProject={() => setCreateProjectOpen(true)}
                    onEditProject={() => setViewProjectOpen(true)}
                />
            ),
        });
    }, [setPageHeader, tasks, tasksLoading]);

    return (
        <Page>
            <DataTable
                rowData={timelineOfTasks}
                loading={tasksLoading}
                tableConfig={TIMELINE_TABLE_CONFIG}
                onRowClick={(task) => setSelectedTask?.(task)}
            />

            {selectedTask && (
                <ViewTaskModal
                    isOpen={Boolean(selectedTask)}
                    task={selectedTask}
                    onClose={() => setSelectedTask(null)}
                />
            )}
        </Page>
    );
}

export default TimelinePage;